import React from "react";
import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";

import {
  ChevronRight,
  FileText,
  ClipboardList,
  Eye,
  Upload,
  Sparkles,
  Zap,
  Plus,
  X,
} from "lucide-react";

import ScoreItem from "../../components/Common/ScoreItem";
import { projectInfo } from "../../utils/info";
import { useStarterPlan } from "../../contexts/OrganizationPlanContext";

export default function BugReport({ darkMode, onBack }) {
  const navigate = useNavigate();
  const location = useLocation();

  const prefillDataArray = location.state?.prefillDataArray || 
    (location.state?.prefillData ? [location.state.prefillData] : null);

  const borderColor = darkMode ? "border-gray-800/50" : "border-gray-200";
  const cardBg = darkMode ? "bg-[#161922]" : "bg-white";
  const inputBg = darkMode ? "bg-[#0f111a]" : "bg-gray-50";

  const { currentUser, selectedProject } = useSelector((state) => state.auth);
  const starterBypass = useStarterPlan();
  const canCreateBugs =
    starterBypass ||
    (currentUser?.role !== "org_admin" && selectedProject?.user_role === "qa");

  // 👉 FIX: Safely parse `screenshots` array into the `prefilledImages` attribute
  const initialTabs = prefillDataArray ? prefillDataArray.map((data, index) => ({
    id: index + 1,
    tabTitle: data.title || `Bug ${index + 1}`,
    formData: {
      title: data.title || "",
      description: data.description || "",
      actual_result: data.actual_result || "",
      expected_result: data.expected_result || "",
      adjecent_testcaseid: data.adjecent_testcaseid || "",
      severity: "",
      priority: "",
      module: data.module || "",
      environment: "",
      assigned_to: [],
    },
    steps: data.steps && data.steps.length > 0
      ? data.steps.map((action, idx) => ({ id: idx + 1, action: typeof action === "string" ? action : action.action || "" }))
      : [{ id: 1, action: "" }],
    prefilledImages: Array.isArray(data.screenshots) ? data.screenshots : [], // Explicit mapping for screenshots
    images: [],
  })) : [{
    id: 1,
    tabTitle: "New Bug",
    formData: { title: "", description: "", actual_result: "", expected_result: "", adjecent_testcaseid: "", severity: "", priority: "", module: "", environment: "", assigned_to: [] },
    steps: [{ id: 1, action: "" }],
    prefilledImages: [],
    images: [],
  }];

  const [tabs, setTabs] = React.useState(initialTabs);
  const [activeTabId, setActiveTabId] = React.useState(initialTabs[0].id);

  const activeTab = tabs.find(t => t.id === activeTabId) || tabs[0];

  const updateActiveTab = (updates) => {
    setTabs(prev => prev.map(t => t.id === activeTabId ? { ...t, ...updates } : t));
  };

  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isEnhancing, setIsEnhancing] = React.useState(false);

  const [assigneeModalOpen, setAssigneeModalOpen] = React.useState(false);
  const [projectMembers, setProjectMembers] = React.useState([]);
  const [projectMembersLoading, setProjectMembersLoading] = React.useState(false);
  const [projectMembersError, setProjectMembersError] = React.useState(false);
  const [previewImage, setPreviewImage] = React.useState(null);

  // Uses the current active tab's images
  const hasImages = activeTab.images.length > 0 || activeTab.prefilledImages.length > 0;

  React.useEffect(() => {
    const projectId = selectedProject?.project_id;
    if (!projectId) {
      setProjectMembers([]);
      return;
    }

    let isCurrent = true;
    setProjectMembers([]);
    setProjectMembersError(false);
    setProjectMembersLoading(true);

    axios.get(`https://api.testoraglobal.in/api/project/id/${projectId}`)
      .then((res) => {
        if (!res.data?.success || !res.data.project) {
          throw new Error(res.data?.message || "Failed to load project members");
        }

        const members = [
          ...(res.data.project.managers || []),
          ...(res.data.project.developers || []),
          ...(res.data.project.qa || []),
          ...(res.data.project.org_admins || []),
        ];
        const uniqueMembers = [];
        const seen = new Set();

        members.forEach((member) => {
          const userId = Number(member?.user_id);
          if (!member || !userId || seen.has(userId)) return;
          seen.add(userId);
          uniqueMembers.push({ ...member, user_id: userId });
        });

        if (isCurrent) {
          setProjectMembers(uniqueMembers.sort((a, b) => (a.full_name || a.email || "").localeCompare(b.full_name || b.email || "")));
        }
      })
      .catch((error) => {
        console.error("Failed to load project members", error);
        if (isCurrent) {
          setProjectMembersError(true);
          toast.error("Failed to load project members");
        }
      })
      .finally(() => {
        if (isCurrent) setProjectMembersLoading(false);
      });

    return () => {
      isCurrent = false;
    };
  }, [selectedProject?.project_id]);

  const selectedAssigneeIds = React.useMemo(() => {
    const raw = activeTab.formData.assigned_to;
    const list = Array.isArray(raw) ? raw : raw ? [raw] : [];
    return list.map((item) => Number(item)).filter((item) => !Number.isNaN(item));
  }, [activeTab.formData.assigned_to]);

  const selectedAssigneeNames = React.useMemo(() => {
    return projectMembers
      .filter((member) => selectedAssigneeIds.includes(Number(member.user_id)))
      .map((member) => member.full_name || member.email || `User ${member.user_id}`);
  }, [projectMembers, selectedAssigneeIds]);

  const toggleAssignee = (userId) => {
    const current = Array.isArray(activeTab.formData.assigned_to) ? activeTab.formData.assigned_to : [];
    const numericId = Number(userId);
    const exists = current.some((id) => Number(id) === numericId);

    const newAssignedTo = exists
      ? current.filter((id) => Number(id) !== numericId)
      : [...current, numericId];

    updateActiveTab({ formData: { ...activeTab.formData, assigned_to: newAssignedTo } });
  };

  const isAIReady =
    activeTab.formData.title &&
    activeTab.formData.description &&
    activeTab.formData.actual_result &&
    activeTab.formData.expected_result &&
    activeTab.formData.severity &&
    activeTab.formData.priority &&
    activeTab.formData.module &&
    activeTab.formData.environment &&
    selectedAssigneeIds.length > 0 &&
    activeTab.steps.some((s) => s.action.trim()) &&
    hasImages;

  const handleChange = (e) => {
    updateActiveTab({ formData: { ...activeTab.formData, [e.target.name]: e.target.value } });
  };

  const handleStepChange = (index, value) => {
    const updated = [...activeTab.steps];
    updated[index].action = value;
    updateActiveTab({ steps: updated });
  };

  const addStep = () => {
    updateActiveTab({ steps: [...activeTab.steps, { id: Date.now(), action: "" }] });
  };

  const removeStep = (id) => {
    if (activeTab.steps.length > 1) {
      updateActiveTab({ steps: activeTab.steps.filter((step) => step.id !== id) });
    }
  };

  const validateForm = () => {
    const { formData, steps } = activeTab;
    if (!formData.title?.trim()) { toast.error("Bug Title is required"); return false; }
    if (!formData.description?.trim()) { toast.error("Bug Description is required"); return false; }
    if (!formData.actual_result?.trim()) { toast.error("Actual Result is required"); return false; }
    if (!formData.expected_result?.trim()) { toast.error("Expected Result is required"); return false; }
    if (!formData.severity) { toast.error("Please select Severity"); return false; }
    if (!formData.priority) { toast.error("Please select Priority"); return false; }
    if (!formData.module?.trim()) { toast.error("Module is required"); return false; }
    if (!formData.environment?.trim()) { toast.error("Environment is required"); return false; }
    if (selectedAssigneeIds.length === 0) { toast.error("Assigned To is required"); return false; }
    if (!steps.length || steps.every((step) => !step.action.trim())) { toast.error("Please add at least one step"); return false; }
    return true;
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (activeTab.images.length + activeTab.prefilledImages.length + files.length > 3) {
      toast.error("Maximum 3 images allowed total");
      return;
    }
    const newImages = files.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
    }));
    updateActiveTab({ images: [...activeTab.images, ...newImages] });
  };

  const removeImage = (index) => {
    const updated = [...activeTab.images];
    updated.splice(index, 1);
    updateActiveTab({ images: updated });
  };

  const handleSubmit = async () => {
    if (!canCreateBugs) return;
    if (!validateForm()) return;
    if (!currentUser || !selectedProject) {
      toast.error("Select a project and login before submitting a bug.");
      return;
    }

    try {
      setIsSubmitting(true);

      let imageUrls = [...activeTab.prefilledImages];
      
      if (activeTab.images.length > 0) {
        const formDataImg = new FormData();
        activeTab.images.forEach((img) => formDataImg.append("images", img.file));
        formDataImg.append("project_id", String(selectedProject.project_id));

        const uploadRes = await axios.post(
          "https://api.testoraglobal.in/api/bug/upload-images",
          formDataImg,
        );
        imageUrls = [...imageUrls, ...uploadRes.data.urls];
      }

      const payload = {
        ...activeTab.formData,
        assigned_to: selectedAssigneeIds,
        steps: activeTab.steps,
        images: imageUrls,
        project_id: selectedProject.project_id,
        raised_by: currentUser.user_name,
        user_id: currentUser.user_id,
        organization_id: currentUser.org_id,
      };

      const res = await axios.post("https://api.testoraglobal.in/api/bug/create", payload);
      toast.success(res.data.message);

      const remainingTabs = tabs.filter(t => t.id !== activeTabId);
      
      if (remainingTabs.length > 0) {
        setTabs(remainingTabs);
        setActiveTabId(remainingTabs[0].id);
      } else {
        setTimeout(() => navigate("/bug-optimizer"), 1000); 
      }
    } catch (error) {
      toast.error("Failed to create bug");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAIEnhance = async () => {
    if (!activeTab.formData.title && !activeTab.formData.description) {
      toast.error("Enter some data first");
      return;
    }

    try {
      setIsEnhancing(true);

      const payload = {
        ...activeTab.formData,
        steps: activeTab.steps,
        project_info: projectInfo,
        project_id: selectedProject?.project_id,
      };

      const res = await axios.post("https://api.testoraglobal.in/api/ai/analyze", payload);
      const aiData = res.data;

      const allowedSeverity = ["Critical", "High", "Medium", "Low"];
      const allowedPriority = ["P0", "P1", "P2", "P3"];

      updateActiveTab({
        formData: {
          ...activeTab.formData,
          title: aiData.title || activeTab.formData.title,
          description: aiData.description || activeTab.formData.description,
          actual_result: aiData.actual_result || activeTab.formData.actual_result,
          expected_result: aiData.expected_result || activeTab.formData.expected_result,
          severity: allowedSeverity.includes(aiData.severity) ? aiData.severity : activeTab.formData.severity,
          priority: allowedPriority.includes(aiData.priority) ? aiData.priority : activeTab.formData.priority,
          module: aiData.module || activeTab.formData.module,
          environment: aiData.environment || activeTab.formData.environment,
        },
        steps: Array.isArray(aiData.steps) 
          ? aiData.steps.map((step, i) => ({ id: i + 1, action: typeof step === "string" ? step : step.action || "" }))
          : activeTab.steps
      });

      toast.success("AI Enhanced ");
    } catch (error) {
      console.error(error);
      toast.error("AI Enhance failed try again");
    } finally {
      setIsEnhancing(false);
    }
  };

  return (
    <>
      <div className="px-8 pb-12 max-w-300 mx-auto animate-in slide-in-from-right-4 duration-500">
        <button
          onClick={() => navigate("/bug-optimizer")}
          className="flex items-center gap-2 text-gray-500 hover:text-purple-500 transition-colors mb-6 text-sm font-medium"
        >
          <ChevronRight size={16} className="rotate-180" />
          Back to Optimizer
        </button>

        <div className="flex justify-between items-start mb-4">
          <div>
            <h2 className="text-4xl font-bold tracking-tight">Report New Bug</h2>
            <p className="text-gray-500 mt-2">Document Bugs with AI-assisted clarity.</p>
          </div>
          <div className="flex gap-3 mt-4">
            <button
              onClick={() => navigate("/bug-optimizer")}
              className={`px-5 py-2.5 rounded-xl text-sm transition cursor-pointer font-bold border ${borderColor} ${
                darkMode ? "text-gray-400 hover:text-[#f43f5e] hover:bg-white/5" : "text-gray-600 hover:text-[#f43f5e]"
              }`}
            >
              Discard Session
            </button>
            {canCreateBugs && (
              <button
                onClick={handleSubmit}
                disabled={isSubmitting || isEnhancing}
                className="px-5 py-2.5 rounded-xl bg-purple-600 hover:bg-purple-700 transition cursor-pointer text-white text-sm font-bold shadow-lg shadow-purple-500/20 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? "Submitting..." : `Submit Bug ${tabs.length > 1 ? `(${tabs.length} left)` : ""}`}
                <ChevronRight size={16} />
              </button>
            )}
          </div>
        </div>

        {tabs.length > 1 && (
          <div className="flex gap-2 overflow-x-auto mb-6 custom-scrollbar pb-2">
            {tabs.map((tab, idx) => (
              <button
                key={tab.id}
                onClick={() => setActiveTabId(tab.id)}
                className={`px-4 py-2.5 rounded-xl text-sm font-bold border transition-all whitespace-nowrap flex items-center gap-2 ${
                  activeTabId === tab.id
                    ? "bg-purple-600/10 border-purple-500 text-purple-400"
                    : `border-transparent ${inputBg} text-gray-500 hover:text-white`
                }`}
              >
                <div className={`w-2 h-2 rounded-full ${activeTabId === tab.id ? 'bg-purple-500' : 'bg-gray-600'}`}></div>
                Bug {idx + 1}: {tab.tabTitle.length > 20 ? tab.tabTitle.substring(0, 20) + "..." : tab.tabTitle}
              </button>
            ))}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <div className={`p-8 rounded-3xl border ${cardBg} ${borderColor} shadow-sm space-y-6`}>
              <h3 className="text-lg font-bold flex items-center gap-2">
                <FileText size={18} className="text-purple-500" /> General Information
              </h3>

              <div className="space-y-1">
                <label className="text-sm font-medium">Bug Title</label>
                <input
                  name="title"
                  value={activeTab.formData.title}
                  onChange={handleChange}
                  placeholder="Enter bug title (e.g., Login button not working)"
                  className={`w-full px-4 py-3.5 rounded-2xl border ${inputBg} ${borderColor}`}
                />
              </div>

              <div className="space-y-1">
                <label className="text-sm font-medium">Bug Description</label>
                <input
                  name="description"
                  value={activeTab.formData.description}
                  onChange={handleChange}
                  placeholder="Briefly describe the issue"
                  className={`w-full px-4 py-3.5 rounded-2xl border ${inputBg} ${borderColor}`}
                />
              </div>

              <div className="space-y-1">
                <label className="text-sm font-medium">Actual Result</label>
                <input
                  name="actual_result"
                  value={activeTab.formData.actual_result}
                  onChange={handleChange}
                  placeholder="What actually happened?"
                  className={`w-full px-4 py-3.5 rounded-2xl border ${inputBg} ${borderColor}`}
                />
              </div>

              <div className="space-y-1">
                <label className="text-sm font-medium">Expected Result</label>
                <input
                  name="expected_result"
                  value={activeTab.formData.expected_result}
                  onChange={handleChange}
                  placeholder="What was expected?"
                  className={`w-full px-4 py-3.5 rounded-2xl border ${inputBg} ${borderColor}`}
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-1">
                  <label className="text-sm font-medium">Severity</label>
                  <select
                    name="severity"
                    value={activeTab.formData.severity}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 rounded-xl border ${inputBg} ${borderColor}`}
                  >
                    <option value="">Select severity</option>
                    <option>Critical</option>
                    <option>High</option>
                    <option>Medium</option>
                    <option>Low</option>
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="text-sm font-medium">Priority</label>
                  <select
                    name="priority"
                    value={activeTab.formData.priority}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 rounded-xl border ${inputBg} ${borderColor}`}
                  >
                    <option value="">Select priority</option>
                    <option>P0</option>
                    <option>P1</option>
                    <option>P2</option>
                    <option>P3</option>
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="text-sm font-medium">Module</label>
                  <input
                    name="module"
                    value={activeTab.formData.module}
                    onChange={handleChange}
                    placeholder="e.g., Dashboard"
                    className={`w-full px-4 py-3 rounded-xl border ${inputBg} ${borderColor}`}
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-sm font-medium">adjecent_testcaseid</label>
                  <input
                    name="adjecent_testcaseid"
                    value={activeTab.formData.adjecent_testcaseid}
                    onChange={handleChange}
                    placeholder="Optional test case ID"
                    className={`w-full px-4 py-3 rounded-xl border ${inputBg} ${borderColor}`}
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-sm font-medium">Environment</label>
                  <input
                    name="environment"
                    value={activeTab.formData.environment}
                    onChange={handleChange}
                    placeholder="e.g., UAT / Dev / Prod"
                    className={`w-full px-4 py-3 rounded-xl border ${inputBg} ${borderColor}`}
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-sm font-medium">Assigned To</label>
                  <button
                    type="button"
                    onClick={() => setAssigneeModalOpen(true)}
                    className={`w-full px-4 py-3 rounded-xl border text-left ${inputBg} ${borderColor} ${selectedAssigneeIds.length > 0 ? "text-white" : "text-gray-400"}`}
                  >
                    {selectedAssigneeNames.length > 0 ? selectedAssigneeNames.join(", ") : "Select project members"}
                  </button>
                </div>
              </div>
            </div>

            <div className={`p-8 rounded-3xl border ${cardBg} ${borderColor}`}>
              <h3 className="text-lg font-bold flex items-center gap-2">
                <ClipboardList size={18} className="text-purple-500" />
                Steps to Reproduce
              </h3>

              {activeTab.steps.map((step, index) => (
                <div key={step.id} className="flex gap-3 mt-3">
                  <input
                    value={step.action}
                    onChange={(e) => handleStepChange(index, e.target.value)}
                    placeholder={`Step ${index + 1}`}
                    className={`flex-1 px-4 py-2 rounded-xl border ${inputBg} ${borderColor}`}
                  />
                  <button onClick={() => removeStep(step.id)} className="hover:text-[#f43f5e] transition cursor-pointer">
                    <X size={16} />
                  </button>
                </div>
              ))}
              <button onClick={addStep} className="text-purple-500 text-sm flex items-center gap-2 mt-4">
                <Plus size={14} /> Add Step
              </button>
            </div>

            <div className={`p-8 rounded-3xl border ${cardBg} ${borderColor} shadow-sm space-y-6`}>
              <h3 className="text-lg font-bold flex items-center gap-2">
                <Eye size={18} className="text-purple-500" /> Visual Evidence
              </h3>

              <label className={`border-2 border-dashed ${borderColor} rounded-2xl p-10 flex flex-col items-center justify-center bg-gray-900/10 hover:bg-gray-900/30 transition-all cursor-pointer`}>
                <Upload size={24} className="text-purple-500 mb-3" />
                <p className="text-sm font-bold">Upload Images</p>
                <p className="text-xs text-gray-500">Max 3 images total</p>
                <input type="file" multiple accept="image/*" onChange={handleImageUpload} className="hidden" />
              </label>

              <div className="flex gap-3 flex-wrap">
                {activeTab.prefilledImages.map((url, index) => (
                  <div key={`prefilled-${index}`} className="relative group">
                    <img src={url} alt="prefilled" onClick={() => setPreviewImage(url)} className="w-20 h-20 object-cover rounded-lg cursor-pointer border border-indigo-500/50" />
                    <div className="absolute bottom-0 left-0 right-0 bg-indigo-500 text-white text-[9px] text-center font-bold py-0.5 rounded-b-lg">AUTO</div>
                    <button
                      onClick={() => updateActiveTab({ prefilledImages: activeTab.prefilledImages.filter((_, i) => i !== index) })}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition shadow-lg cursor-pointer"
                    >
                      <X size={12} />
                    </button>
                  </div>
                ))}

                {activeTab.images.map((img, index) => (
                  <div key={index} className="relative group">
                    <img src={img.preview} alt="preview" onClick={() => setPreviewImage(img.preview)} className="w-20 h-20 object-cover rounded-lg cursor-pointer border" />
                    <button onClick={() => removeImage(index)} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition shadow-lg cursor-pointer">
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className={`p-6 rounded-3xl border ${cardBg} ${borderColor}`}>
              <h4 className="text-xs font-bold text-gray-500 uppercase mb-4">Quality Score</h4>
              <ul className="space-y-3">
                <ScoreItem label="Description provided" checked={!!activeTab.formData.description} />
                <ScoreItem label="Steps to reproduce" checked={activeTab.steps.some((s) => s.action.trim())} />
                <ScoreItem label="Expected vs Actual" checked={activeTab.formData.actual_result && activeTab.formData.expected_result} />
                <ScoreItem label="Visual evidence" checked={hasImages} />
              </ul>
            </div>
            
            <div className={`p-6 rounded-3xl border border-purple-500/30 bg-purple-600/5 backdrop-blur-md relative overflow-hidden`}>
              <div className="absolute top-0 right-0 p-4 opacity-10">
                <Sparkles size={60} className="text-purple-500" />
              </div>
              <h4 className="text-sm font-bold flex items-center gap-2 mb-4">
                <Sparkles size={16} className="text-purple-400" /> AI Enhancer
              </h4>
              <p className="text-xs text-gray-400 leading-relaxed mb-6">
                Our AI will analyze your bug details and optimize the title, description, steps, and suggest potential severity/priority.
              </p>
              
              <button
                onClick={handleAIEnhance}
                disabled={!isAIReady || isEnhancing || isSubmitting}
                className={`w-full py-3 rounded-2xl transition text-xs font-bold shadow-lg flex items-center justify-center gap-2 ${
                  isAIReady && !isSubmitting
                    ? "bg-purple-600 hover:bg-purple-700 text-white cursor-pointer shadow-purple-600/30"
                    : "bg-gray-600 text-gray-300 cursor-not-allowed opacity-50"
                }`}
              >
                {isEnhancing ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Analyzing...
                  </>
                ) : (
                  <>
                    <Zap size={14} fill="currentColor" /> Analyze & Optimize
                  </>
                )}
              </button>
              <p className="text-xs font-bold text-gray-400 mt-3">
                NOTE: Required fields and visual evidence must be provided first.
              </p>
            </div>
          </div>
        </div>
      </div>

      {assigneeModalOpen && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className={`w-full max-w-2xl rounded-3xl border ${cardBg} ${borderColor} p-6 shadow-2xl`}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-xl font-bold">Select Assignees</h3>
              <button type="button" onClick={() => setAssigneeModalOpen(false)} className="text-gray-400 hover:text-white">
                <X size={18} />
              </button>
            </div>

            <div className="max-h-80 overflow-y-auto space-y-2 pr-1">
              {projectMembersLoading ? (
                <p className="text-sm text-gray-400">Loading project members...</p>
              ) : projectMembersError ? (
                <p className="text-sm text-red-400">Unable to load project members. Please try again later.</p>
              ) : projectMembers.length === 0 ? (
                <p className="text-sm text-gray-400">No project members found for this project.</p>
              ) : (
                projectMembers.map((member) => {
                  const memberId = Number(member.user_id);
                  const checked = selectedAssigneeIds.includes(memberId);
                  return (
                    <label key={memberId} className={`flex items-center justify-between w-full rounded-2xl border px-4 py-3 cursor-pointer transition ${checked ? "border-purple-500 bg-purple-500/10" : `${borderColor} ${inputBg}`}`}>
                      <div>
                        <div className="font-medium text-sm">{member.full_name || member.email || `User ${memberId}`}</div>
                        <div className="text-xs text-gray-400">{member.email || "Project member"}</div>
                      </div>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleAssignee(memberId)}
                        className="h-4 w-4 accent-purple-500"
                      />
                    </label>
                  );
                })
              )}
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button type="button" onClick={() => setAssigneeModalOpen(false)} className={`px-4 py-2 rounded-xl border ${borderColor} text-sm`}>Cancel</button>
              <button type="button" onClick={() => setAssigneeModalOpen(false)} className="px-4 py-2 rounded-xl bg-purple-600 text-white text-sm font-bold">Confirm Assignees</button>
            </div>
          </div>
        </div>
      )}

      {previewImage && (
        <div onClick={() => setPreviewImage(null)} className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="relative">
             <img src={previewImage} alt="preview full" className="max-w-[90vw] max-h-[90vh] rounded-xl shadow-2xl object-contain" />
             <button onClick={() => setPreviewImage(null)} className="absolute -top-4 -right-4 bg-red-500 hover:bg-red-600 text-white p-2 rounded-full shadow-lg transition cursor-pointer">
                <X size={20} />
             </button>
          </div>
        </div>
      )}
    </>
  );
}